import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useNotification } from './NotificationProvider';

const SIDEBAR_EXPANDED = 264;
const SIDEBAR_COLLAPSED = 88;
const SIDEBAR_GLOBAL_KEY = '__MLB_SIDEBAR_STATE__';

const ensureGlobalSidebarState = () => {
  if (typeof window === 'undefined') {
    return {
      hoverExpanded: false,
      forcedExpanded: false,
      isCollapsed: true,
      mountCount: 0,
      cleanupTimer: null
    };
  }

  if (!window[SIDEBAR_GLOBAL_KEY]) {
    window[SIDEBAR_GLOBAL_KEY] = {
      hoverExpanded: false,
      forcedExpanded: false,
      isCollapsed: true,
      mountCount: 0,
      cleanupTimer: null
    };
  }

  return window[SIDEBAR_GLOBAL_KEY];
};

const Header = ({ isAuthenticated = true, onLogout, apiConnected = false }) => {
  const { user, userProfile } = useAuth();
  const { notifications = [], removeNotification, clearAllNotifications, showSuccess } = useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  const globalSidebarStateRef = useRef(null);

  if (!globalSidebarStateRef.current) {
    globalSidebarStateRef.current = ensureGlobalSidebarState();
  }

  const globalSidebarState = globalSidebarStateRef.current;
  const profileMenuRef = useRef(null);
  const hoverOpenTimeoutRef = useRef(null);
  const hoverCloseTimeoutRef = useRef(null);
  const hoverExpandedRef = useRef(globalSidebarState?.hoverExpanded || false);
  const forcedExpandedRef = useRef(globalSidebarState?.forcedExpanded || false);
  const sidebarRef = useRef(null);
  const profileImageInputRef = useRef(null);
  const [profileImage, setProfileImage] = useState(null);

  const getProfilePhotoStorageKey = useCallback(() => {
    const accountKey = user?.email || user?.id || 'default';
    return `mlb_profile_photo:${accountKey}`;
  }, [user?.email, user?.id]);

  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return globalSidebarState?.isCollapsed ?? true;
  });
  const [isHoverExpanded, setIsHoverExpanded] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return Boolean(globalSidebarState?.hoverExpanded);
  });
  const [isForcedExpanded, setIsForcedExpanded] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return Boolean(globalSidebarState?.forcedExpanded);
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return window.matchMedia('(min-width: 1024px)').matches;
  });

  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarYear, setCalendarYear] = useState(() => {
    const base = new Date();
    return base.getFullYear();
  });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const base = new Date();
    return base.getMonth();
  });
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [calendarNotes, setCalendarNotes] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const stored = window.localStorage.getItem('mlb_calendar_notes');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const navigationItems = useMemo(() => ([
    {
      label: 'Panel de Control',
      path: '/dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'Vista general del sistema'
    },
    {
      label: 'Subir Productos',
      path: '/bulk-upload',
      icon: 'Upload',
      tooltip: 'Carga masiva de productos'
    },
    {
      label: 'Historial',
      path: '/upload-history',
      icon: 'History',
      tooltip: 'Historial de cargas'
    },
    {
      label: 'AWS',
      path: '/aws-database-connection',
      icon: 'Database',
      tooltip: 'Conexión a base de datos Amazon'
    },
    {
      label: 'Exportar Datos',
      path: '/data-export-generator',
      icon: 'FileDown',
      tooltip: 'Generador de exportación de datos'
    },
    {
      label: 'Configuración',
      path: '/api-configuration',
      icon: 'Settings',
      tooltip: 'Configuración de API'
    }
  ]), []);

  // Consultar el permiso vigente sin depender del metadata antiguo
  // almacenado en la sesión del navegador.
  const [priceUpdaterAccess, setPriceUpdaterAccess] = useState({
    userId: null,
    enabled: false
  });

  useEffect(() => {
    const userId = user?.id;

    if (!userId) {
      setPriceUpdaterAccess({ userId: null, enabled: false });
      return;
    }

    let active = true;
    let checking = false;

    const checkPermission = async () => {
      if (!active || checking) return;
      checking = true;

      try {
        const { data, error } = await supabase.auth.getUser();

        if (active) {
          setPriceUpdaterAccess({
            userId,
            enabled:
              !error &&
              data?.user?.id === userId &&
              data?.user?.app_metadata?.permissions
                ?.price_updater === true
          });
        }
      } catch (error) {
        if (active) {
          setPriceUpdaterAccess({
            userId,
            enabled: false
          });
        }
      } finally {
        checking = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkPermission();
      }
    };

    checkPermission();

    window.addEventListener('focus', checkPermission);
    document.addEventListener('visibilitychange', onVisibilityChange);

    const interval = window.setInterval(checkPermission, 30000);

    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', checkPermission);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user?.id]);

  const hasPriceUpdaterAccess =
    userProfile?.role === 'admin' ||
    (
      priceUpdaterAccess.userId === user?.id &&
      priceUpdaterAccess.enabled === true
    );

  const visibleNavigationItems = useMemo(() => {
    const items = [...navigationItems];

    if (hasPriceUpdaterAccess) {
      items.push({
        label: 'Actualizador de precios',
        path: '/price-updater',
        icon: 'RefreshCw',
        tooltip: 'Sincronización de precios Amazon → Mercado Libre'
      });
    }

    if (userProfile?.role === 'admin') {
      items.push({
        label: 'Peso y dimensiones',
        path: '/admin/mco-classifier',
        icon: 'Truck',
        tooltip: 'Peso y dimensiones de productos'
      });
      items.push({
        label: 'Administración',
        path: '/admin/users',
        icon: 'Users',
        tooltip: 'Administración de usuarios'
      });
    }

    return items;
  }, [
    navigationItems,
    hasPriceUpdaterAccess,
    userProfile?.role
  ]);

  const navigationIconStyles = {
    '/dashboard': 'text-sky-500 bg-sky-500/10',
    '/bulk-upload': 'text-blue-500 bg-blue-500/10',
    '/upload-history': 'text-violet-500 bg-violet-500/10',
    '/aws-database-connection': 'text-emerald-500 bg-emerald-500/10',
    '/data-export-generator': 'text-cyan-500 bg-cyan-500/10',
    '/api-configuration': 'text-amber-500 bg-amber-500/10',
    '/price-updater': 'text-lime-500 bg-lime-500/10',
    '/admin/mco-classifier': 'text-fuchsia-500 bg-fuchsia-500/10',
    '/admin/users': 'text-rose-500 bg-rose-500/10'
  };

  useEffect(() => {
    try {
      const savedPhoto = window.localStorage.getItem(
        getProfilePhotoStorageKey()
      );
      setProfileImage(savedPhoto || null);
    } catch (error) {
      console.warn('No se pudo cargar la foto de perfil:', error);
      setProfileImage(null);
    }
  }, [getProfilePhotoStorageKey]);

  const handleNavigation = useCallback((path) => {
    if (isDesktop && isCollapsed) {
      if (hoverCloseTimeoutRef.current) {
        clearTimeout(hoverCloseTimeoutRef.current);
        hoverCloseTimeoutRef.current = null;
      }
      if (hoverOpenTimeoutRef.current) {
        clearTimeout(hoverOpenTimeoutRef.current);
        hoverOpenTimeoutRef.current = null;
      }

      hoverExpandedRef.current = true;
      forcedExpandedRef.current = true;
      setIsHoverExpanded(true);
      setIsForcedExpanded(true);

      if (globalSidebarStateRef.current) {
        globalSidebarStateRef.current.hoverExpanded = true;
        globalSidebarStateRef.current.forcedExpanded = true;
      }
    }

    navigate(path);
    setIsMobileMenuOpen(false);
    setIsProfileMenuOpen(false);
  }, [navigate, isDesktop, isCollapsed]);

  const handleProfileImageChange = useCallback((event) => {
    const input = event?.target;
    const file = input?.files?.[0];

    if (!file) return;

    if (!file.type?.startsWith('image/')) {
      if (input) input.value = '';
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const image = new Image();

      image.onload = () => {
        try {
          // Recorte cuadrado centrado + reducción.
          // Evita guardar una foto enorme en el navegador.
          const sourceSize = Math.min(
            image.naturalWidth,
            image.naturalHeight
          );

          const sourceX =
            (image.naturalWidth - sourceSize) / 2;

          const sourceY =
            (image.naturalHeight - sourceSize) / 2;

          const canvas = document.createElement('canvas');
          canvas.width = 256;
          canvas.height = 256;

          const context = canvas.getContext('2d');

          if (!context) {
            throw new Error('No fue posible procesar la imagen');
          }

          context.drawImage(
            image,
            sourceX,
            sourceY,
            sourceSize,
            sourceSize,
            0,
            0,
            256,
            256
          );

          const optimizedPhoto =
            canvas.toDataURL('image/jpeg', 0.82);

          window.localStorage.setItem(
            getProfilePhotoStorageKey(),
            optimizedPhoto
          );

          setProfileImage(optimizedPhoto);
          setIsProfileMenuOpen(false);

          showSuccess?.('Foto de perfil actualizada', {
            title: 'Perfil'
          });
        } catch (error) {
          console.error(
            'No se pudo guardar la foto de perfil:',
            error
          );
        } finally {
          if (input) input.value = '';
        }
      };

      image.onerror = () => {
        console.error('La imagen seleccionada no pudo procesarse');
        if (input) input.value = '';
      };

      image.src = reader.result;
    };

    reader.onerror = () => {
      console.error('No se pudo leer la imagen seleccionada');
      if (input) input.value = '';
    };

    reader.readAsDataURL(file);
  }, [getProfilePhotoStorageKey, showSuccess]);

  const handleRemoveProfileImage = useCallback(() => {
    try {
      window.localStorage.removeItem(
        getProfilePhotoStorageKey()
      );
    } catch (error) {
      console.warn(
        'No se pudo eliminar la foto guardada:',
        error
      );
    }

    setProfileImage(null);

    showSuccess?.('Foto de perfil eliminada', {
      title: 'Perfil'
    });
  }, [getProfilePhotoStorageKey, showSuccess]);

  const handleLogout = useCallback(() => {
    setIsProfileMenuOpen(false);

    try {
      if (onLogout) {
        onLogout();
      }

      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 100);
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login', { replace: true });
    }
  }, [navigate, onLogout]);

  const updateSidebarState = useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const root = document.documentElement;
    const body = document.body;

    let widthValue = '0px';
    const collapsedForDesktop = isCollapsed && !isHoverExpanded && !isForcedExpanded;

    if (isAuthenticated) {
      body.classList.add('app-dashboard-skin');
    } else {
      body.classList.remove('app-dashboard-skin');
    }

    if (isAuthenticated) {
      if (isDesktop) {
        widthValue = collapsedForDesktop ? `${SIDEBAR_COLLAPSED}px` : `${SIDEBAR_EXPANDED}px`;
      } else {
        widthValue = isMobileMenuOpen ? `${SIDEBAR_EXPANDED}px` : '0px';
      }
    }

    root.style.setProperty('--sidebar-current-width', widthValue);

    body.classList.remove('sidebar-expanded', 'sidebar-collapsed', 'sidebar-hidden');

    if (!isAuthenticated) {
      body.classList.add('sidebar-hidden');
    } else if (isDesktop) {
      body.classList.add(collapsedForDesktop ? 'sidebar-collapsed' : 'sidebar-expanded');
    } else {
      body.classList.add(isMobileMenuOpen ? 'sidebar-expanded' : 'sidebar-hidden');
    }
  }, [isAuthenticated, isCollapsed, isDesktop, isMobileMenuOpen, isHoverExpanded, isForcedExpanded]);

  useEffect(() => {
    updateSidebarState();
  }, [updateSidebarState]);

  useEffect(() => {
    const globalState = globalSidebarStateRef.current;

    if (globalState) {
      globalState.mountCount = (globalState.mountCount || 0) + 1;
      if (globalState.cleanupTimer && typeof window !== 'undefined') {
        window.clearTimeout(globalState.cleanupTimer);
        globalState.cleanupTimer = null;
      }
    }

    return () => {
      const state = globalSidebarStateRef.current;

      if (!state) {
        return;
      }

      state.mountCount = Math.max(0, (state.mountCount || 1) - 1);

      const performCleanup = () => {
        if ((state.mountCount || 0) === 0) {
          if (typeof document !== 'undefined') {
            const root = document.documentElement;
            const body = document.body;
            root.style.setProperty('--sidebar-current-width', '0px');
            body.classList.remove('sidebar-expanded', 'sidebar-collapsed', 'sidebar-hidden', 'app-dashboard-skin');
            body.style.overflow = '';
          }

          state.hoverExpanded = false;
          state.forcedExpanded = false;
          state.isCollapsed = true;
        }
      };

      if (typeof window !== 'undefined') {
        if (state.cleanupTimer) {
          window.clearTimeout(state.cleanupTimer);
        }
        state.cleanupTimer = window.setTimeout(() => {
          performCleanup();
          state.cleanupTimer = null;
        }, 200);
      } else {
        performCleanup();
      }
    };
  }, []);

  useEffect(() => {
    hoverExpandedRef.current = isHoverExpanded;
    if (globalSidebarStateRef.current) {
      globalSidebarStateRef.current.hoverExpanded = isHoverExpanded;
    }
  }, [isHoverExpanded]);

  useEffect(() => {
    forcedExpandedRef.current = isForcedExpanded;
    if (globalSidebarStateRef.current) {
      globalSidebarStateRef.current.forcedExpanded = isForcedExpanded;
    }
  }, [isForcedExpanded]);

  useEffect(() => {
    if (globalSidebarStateRef.current) {
      globalSidebarStateRef.current.isCollapsed = isCollapsed;
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(min-width: 1024px)');

    const handleChange = (event) => {
      setIsDesktop(event.matches);
      setIsHoverExpanded(false);

      if (event.matches) {
        setIsMobileMenuOpen(false);
      } else {
        setIsCollapsed(false);
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useEffect(() => {
    if (!isDesktop && isMobileMenuOpen) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }

    document.body.style.overflow = '';
    return undefined;
  }, [isDesktop, isMobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileMenuOpen && profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileMenuOpen]);

  useEffect(() => {
    if (!isCollapsed) {
      setIsHoverExpanded(false);
    }
  }, [isCollapsed]);

  useEffect(() => () => {
    if (hoverOpenTimeoutRef.current) {
      clearTimeout(hoverOpenTimeoutRef.current);
      hoverOpenTimeoutRef.current = null;
    }
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  }, []);

  // Persistir notas del calendario en localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('mlb_calendar_notes', JSON.stringify(calendarNotes));
    } catch {
      // ignorar errores de almacenamiento
    }
  }, [calendarNotes]);

  if (!isAuthenticated) {
    return null;
  }

  const effectiveIsCollapsed = isDesktop ? (isCollapsed && !isHoverExpanded && !isForcedExpanded) : false;
  const showLabels = isDesktop ? !effectiveIsCollapsed : true;
  const notificationsCount = notifications?.length || 0;

  // Datos básicos para el calendario
  const today = new Date();
  const formattedDate = today.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const displayYear = calendarYear;
  const displayMonth = calendarMonth;
  const monthLabel = new Date(displayYear, displayMonth, 1).toLocaleDateString('es-ES', {
    month: 'long',
    year: 'numeric',
  });

  const firstDayOfMonth = new Date(displayYear, displayMonth, 1);
  let start = firstDayOfMonth.getDay(); // 0 = domingo
  // Ajustar para que la semana inicie en lunes (0 = lunes)
  start = (start + 6) % 7;
  const daysInMonth = new Date(displayYear, displayMonth + 1, 0).getDate();
  const calendarCells = [];
  for (let i = 0; i < start; i += 1) {
    calendarCells.push(null);
  }
  for (let d = 1; d <= daysInMonth; d += 1) {
    calendarCells.push(d);
  }
  while (calendarCells.length % 7 !== 0) {
    calendarCells.push(null);
  }

  const handleChangeMonth = (offset) => {
    setSelectedDateKey(null);
    setNoteText('');
    if (offset === -1) {
      setCalendarMonth((prevMonth) => {
        if (prevMonth === 0) {
          setCalendarYear((prevYear) => prevYear - 1);
          return 11;
        }
        return prevMonth - 1;
      });
    } else if (offset === 1) {
      setCalendarMonth((prevMonth) => {
        if (prevMonth === 11) {
          setCalendarYear((prevYear) => prevYear + 1);
          return 0;
        }
        return prevMonth + 1;
      });
    }
  };

  const handleSaveNote = () => {
    if (!selectedDateKey) return;
    const trimmed = noteText.trim();
    setCalendarNotes((prev) => ({
      ...prev,
      [selectedDateKey]: trimmed,
    }));
    if (trimmed) {
      showSuccess?.('Nota guardada en el calendario', { title: 'Calendario' });
    }
  };

  const handleDeleteNote = () => {
    if (!selectedDateKey) return;
    setCalendarNotes((prev) => {
      const next = { ...prev };
      delete next[selectedDateKey];
      return next;
    });
    setNoteText('');
    showSuccess?.('Nota eliminada del calendario', { title: 'Calendario' });
  };

  const sidebarClasses = [
    'fixed inset-y-0 left-0 z-[1000] flex flex-col bg-surface border-r border-border shadow-soft transition-[transform,width] duration-500 ease-in-out overflow-hidden'
  ];

  if (isDesktop) {
    sidebarClasses.push(effectiveIsCollapsed ? 'w-[88px]' : 'w-[264px]');
    sidebarClasses.push('translate-x-0');
  } else {
    sidebarClasses.push('w-[264px]');
    sidebarClasses.push(isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full');
  }

  return (
    <>
      {!isDesktop && !isMobileMenuOpen && (
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="fixed top-4 left-4 z-[995] flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-soft transition-colors duration-200 ease-in-out lg:hidden"
          aria-label="Abrir menú de navegación"
        >
          <Icon name="Menu" size={20} />
        </button>
      )}

      {!isDesktop && isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-[990] bg-black/40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <aside
        ref={sidebarRef}
        className={sidebarClasses.join(' ')}
        onMouseEnter={() => {
          if (!isDesktop) {
            return;
          }

          if (hoverCloseTimeoutRef.current) {
            clearTimeout(hoverCloseTimeoutRef.current);
            hoverCloseTimeoutRef.current = null;
          }

          if (hoverOpenTimeoutRef.current) {
            clearTimeout(hoverOpenTimeoutRef.current);
            hoverOpenTimeoutRef.current = null;
          }

          if (isCollapsed && !hoverExpandedRef.current) {
            hoverExpandedRef.current = true;
            hoverOpenTimeoutRef.current = setTimeout(() => {
              setIsHoverExpanded(true);
              hoverOpenTimeoutRef.current = null;
            }, 80);
          }
        }}
        onMouseLeave={() => {
          if (hoverOpenTimeoutRef.current) {
            clearTimeout(hoverOpenTimeoutRef.current);
            hoverOpenTimeoutRef.current = null;
          }

          if (hoverCloseTimeoutRef.current) {
            clearTimeout(hoverCloseTimeoutRef.current);
            hoverCloseTimeoutRef.current = null;
          }

          if (hoverExpandedRef.current || forcedExpandedRef.current) {
            hoverCloseTimeoutRef.current = setTimeout(() => {
              const sidebarEl = sidebarRef.current;
              if (sidebarEl && sidebarEl.matches(':hover')) {
                hoverCloseTimeoutRef.current = null;
                return;
              }

              if (forcedExpandedRef.current) {
                forcedExpandedRef.current = false;
                setIsForcedExpanded(false);
              }
              if (hoverExpandedRef.current) {
                hoverExpandedRef.current = false;
                setIsHoverExpanded(false);
              }
              hoverCloseTimeoutRef.current = null;
            }, 220);
          }
        }}
      >
        <div className="flex h-16 items-center justify-between px-3 border-b border-border bg-surface/95 backdrop-blur">
          <button
            onClick={() => handleNavigation('/dashboard')}
            className={`flex items-center gap-3 rounded-lg px-2 py-1 transition-colors duration-150 ease-in-out hover:bg-muted ${showLabels ? 'w-full justify-start' : 'w-full justify-center'}`}
            title="Ir al panel de control"
          >
            <img
              src="/mercadobulk-bag.svg"
              alt="MercadoBulk"
              className="h-10 w-10 shrink-0"
            />
            {showLabels && (
              <div className="text-left">
                <h1 className="text-base font-semibold text-text-primary">MercadoBulk</h1>
              </div>
            )}
          </button>

          {isDesktop ? (
            <div className="hidden lg:block h-8 w-8" />
          ) : null}
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-1">
          {visibleNavigationItems
            .filter((item) => ![
              '/aws-database-connection',
              '/data-export-generator'
            ].includes(item?.path))
            .map((item) => {
            const isActive = location?.pathname === item?.path;
            return (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-150 ease-in-out ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-text-secondary hover:bg-muted hover:text-text-primary'
                } ${showLabels ? 'justify-start' : 'justify-center'}`}
                title={item?.tooltip || item?.label}
                aria-label={item?.label}
              >
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-200 ease-out group-hover:scale-110 ${
                    isActive
                      ? (item?.path === '/admin/mco-classifier'
                          ? 'bg-white text-fuchsia-600'
                          : 'bg-white/15 text-primary-foreground')
                      : (navigationIconStyles[item?.path] || 'text-text-secondary bg-muted/60')
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                </div>

                {showLabels && (
                  <span className="font-medium whitespace-nowrap">
                    {item?.label}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="mt-auto border-t border-border px-3 py-2 space-y-2">
          {/* Botón de calendario encima de notificaciones */}
          <div className={`${showLabels ? 'mb-1' : 'mb-1 flex justify-center'}`}>
            <button
              type="button"
              onClick={() => setIsCalendarOpen(true)}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors duration-150 ease-in-out hover:bg-muted hover:text-text-primary ${showLabels ? 'w-full justify-between' : 'justify-center w-full'}`}
              aria-label="Calendario"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-500/10 transition-transform duration-200 hover:scale-110">
                  <Icon name="Calendar" size={16} className="text-indigo-500" />
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-indigo-700 dark:text-indigo-200">
                    {today.getDate()}
                  </span>
                </div>
                {showLabels && (
                  <div className="flex flex-col leading-tight min-w-0">
                    <span className="text-xs font-medium text-text-primary">Calendario</span>
                    <span className="text-[11px] text-text-secondary truncate">{formattedDate}</span>
                  </div>
                )}
              </div>
            </button>
          </div>
          {/* Centro de notificaciones en el sidebar - abre panel flotante */}
          <div className={`space-y-2 relative ${showLabels ? '' : 'flex justify-center'}`}>
            <button
              onClick={() => setIsNotificationsOpen(true)}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors duration-150 ease-in-out hover:bg-muted hover:text-text-primary ${showLabels ? 'w-full justify-between' : 'justify-center w-full'}`}
              aria-label="Notificaciones"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-500/10 transition-transform duration-200 hover:scale-110">
                  <Icon name="Bell" size={16} className="text-rose-500" />
                  {notificationsCount > 0 && (
                    <>
                      <span className="absolute inline-flex h-6 w-6 rounded-full bg-error/60 opacity-60 animate-ping" />
                      <span className="absolute inline-flex items-center justify-center rounded-full bg-error text-error-foreground text-[11px] font-semibold h-5 min-w-[1.5rem] px-1.5 leading-none animate-bounce">
                        {notificationsCount}
                      </span>
                    </>
                  )}
                </div>
                {showLabels && (
                  <span className="font-medium truncate">Notificaciones</span>
                )}
              </div>
            </button>
          </div>
          <div className={`${showLabels ? 'flex items-center gap-3 rounded-lg bg-muted px-3 py-2' : 'flex items-center justify-center'}`}>
            <div className={`flex items-center gap-2 ${showLabels ? '' : 'justify-center'}`}>
              <div className="h-2 w-2 rounded-full bg-success animate-pulse" />
              {showLabels && (
                <span className="text-xs font-medium text-success">
                  API Conectada
                </span>
              )}
            </div>
          </div>

          <button
            onClick={() => {
              const rootEl = document.documentElement;
              const isDark = rootEl.classList.toggle('dark');
              try { localStorage.setItem('theme', isDark ? 'dark' : 'light'); } catch {}
            }}
            className={`group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-text-secondary transition-all duration-300 ease-in-out hover:bg-muted hover:text-text-primary ${
              showLabels ? 'justify-start' : 'justify-center'
            }`}
            aria-label="Cambiar tema"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-500/10 dark:bg-amber-500/10 transition-all duration-300 ease-in-out group-hover:scale-110">
              <Icon
                name="Moon"
                size={16}
                className="text-violet-500 dark:hidden"
              />
              <Icon
                name="Sun"
                size={16}
                className="hidden text-amber-400 dark:block"
              />
            </div>

            {showLabels && (
              <span className="font-medium whitespace-nowrap">
                Tema
              </span>
            )}
          </button>

          <div className={`relative ${showLabels ? '' : 'flex justify-center'}`} ref={profileMenuRef}>
            <button
              onClick={() => setIsProfileMenuOpen(prev => !prev)}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 transition-colors duration-150 ease-in-out hover:bg-muted ${showLabels ? 'w-full justify-between' : 'justify-center'}`}
              aria-label="Menú de perfil"
            >
              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full ${
                  profileImage ? 'bg-muted' : 'bg-primary'
                }`}
              >
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Foto de perfil"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Icon
                    name="User"
                    size={16}
                    color="white"
                  />
                )}
              </div>
              {showLabels && (
                <div className="min-w-0 flex-1 text-left">
                  <p className="text-sm font-medium text-text-primary" title={user?.email || 'Sin sesión'}>
                    {user?.email || 'Sin sesión'}
                  </p>
                  <p className="text-xs text-text-secondary">Cuenta activa</p>
                </div>
              )}
              {showLabels && (
                <Icon name="ChevronDown" size={16} className="text-text-secondary" />
              )}
            </button>

            <input
              ref={profileImageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleProfileImageChange}
            />

            {isProfileMenuOpen && (
              <div
                className={`absolute w-56 rounded-lg border border-border bg-popover shadow-elevated z-[1010] ${
                  showLabels
                    ? 'bottom-full mb-2 right-0'
                    : 'left-full ml-2 top-1/2 -translate-y-1/2'
                }`}
              >
                <div className="border-b border-border p-3">
                  <p className="text-sm font-medium text-text-primary">Usuario</p>
                  <p className="truncate text-xs text-text-secondary" title={user?.email}>{user?.email || 'Sin sesión'}</p>
                </div>
                <div className="p-1">
                  <button
                    type="button"
                    onClick={() =>
                      profileImageInputRef.current?.click()
                    }
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors duration-150 ease-in-out hover:bg-muted"
                  >
                    <Icon
                      name="Camera"
                      size={16}
                      className="text-primary"
                    />
                    <span>
                      {profileImage
                        ? 'Cambiar foto'
                        : 'Agregar foto'}
                    </span>
                  </button>

                  {profileImage && (
                    <button
                      type="button"
                      onClick={handleRemoveProfileImage}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-error transition-colors duration-150 ease-in-out hover:bg-muted"
                    >
                      <Icon name="Trash2" size={16} />
                      <span>Quitar foto</span>
                    </button>
                  )}

                  <div className="my-1 border-t border-border" />

                  <button
                    onClick={() => handleNavigation('/api-configuration')}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors duration-150 ease-in-out hover:bg-muted"
                  >
                    <Icon name="Settings" size={16} />
                    <span>Configuración</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-error transition-colors duration-150 ease-in-out hover:bg-muted"
                  >
                    <Icon name="LogOut" size={16} />
                    <span>Cerrar Sesión</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
      {/* Ventana emergente del calendario en el centro de la pantalla */}
      {isCalendarOpen && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsCalendarOpen(false)}
          />
          <div className="relative z-[1110] w-full max-w-md mx-4 rounded-2xl border border-border bg-surface shadow-elevated">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                  <Icon name="Calendar" size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-text-primary">Calendario</h2>
                  <p className="text-xs text-text-secondary capitalize">{monthLabel}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handleChangeMonth(-1)}
                  className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-muted"
                  aria-label="Mes anterior"
                >
                  <Icon name="ChevronLeft" size={14} className="text-text-secondary" />
                </button>
                <button
                  type="button"
                  onClick={() => handleChangeMonth(1)}
                  className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-muted"
                  aria-label="Mes siguiente"
                >
                  <Icon name="ChevronRight" size={14} className="text-text-secondary" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const base = new Date();
                    setCalendarYear(base.getFullYear());
                    setCalendarMonth(base.getMonth());
                    setSelectedDateKey(null);
                    setNoteText('');
                  }}
                  className="ml-1 px-2 py-1 rounded-full text-[10px] text-text-secondary hover:bg-muted"
                >
                  Hoy
                </button>
                <button
                  type="button"
                  onClick={() => setIsCalendarOpen(false)}
                  className="ml-1 flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted"
                  aria-label="Cerrar calendario"
                >
                  <Icon name="X" size={16} className="text-text-secondary" />
                </button>
              </div>
            </div>
            <div className="px-5 py-4 pb-5 text-xs">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] text-text-secondary">Hoy</span>
                <span className="text-[11px] font-medium text-text-primary">{formattedDate}</span>
              </div>
              <div className="grid grid-cols-7 gap-1 mb-2 text-[11px] text-text-secondary">
                {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d) => (
                  <div key={d} className="flex h-6 items-center justify-center font-medium">
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 text-[11px]">
                {calendarCells.map((day, index) => {
                  if (!day) {
                    return <div key={index} className="h-7" />;
                  }
                  const dateKey = `${displayYear}-${String(displayMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const hasNote = Boolean(calendarNotes?.[dateKey]);
                  const isTodayCell =
                    day === today.getDate() &&
                    displayMonth === today.getMonth() &&
                    displayYear === today.getFullYear();
                  const isSelected = selectedDateKey === dateKey;
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        setSelectedDateKey(dateKey);
                        setNoteText(calendarNotes?.[dateKey] || '');
                      }}
                      className={`relative flex h-7 items-center justify-center rounded-full border border-transparent cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-primary text-primary-foreground border-primary shadow-soft'
                          : isTodayCell
                            ? 'bg-primary/10 text-primary border-primary/40'
                            : 'bg-muted/40 text-text-primary hover:bg-muted'
                      }`}
                    >
                      {day}
                      {hasNote && (
                        <span className="absolute bottom-0.5 right-1 h-1.5 w-1.5 rounded-full bg-primary" />
                      )}
                    </button>
                  );
                })}
              </div>

              {selectedDateKey && (
                <div className="mt-4 border-t border-border pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium text-text-primary">
                      {(() => {
                        const [y, m, d] = selectedDateKey.split('-').map(Number);
                        const dateObj = new Date(y, m - 1, d);
                        return dateObj.toLocaleDateString('es-ES', {
                          weekday: 'long',
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        });
                      })()}
                    </span>
                    {calendarNotes?.[selectedDateKey] && (
                      <button
                        type="button"
                        onClick={handleDeleteNote}
                        className="text-[10px] text-error hover:underline"
                      >
                        Borrar nota
                      </button>
                    )}
                  </div>
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-border bg-background px-2 py-1 text-[11px] text-text-primary focus:outline-none focus:ring-1 focus:ring-primary/60"
                    placeholder="Escribe una nota para este día..."
                  />
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleSaveNote}
                      className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-[11px] font-medium hover:bg-primary/90"
                    >
                      Guardar nota
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Ventana emergente de notificaciones en el centro de la pantalla */}
      {isNotificationsOpen && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsNotificationsOpen(false)}
          />
          <div className="relative z-[1110] w-full max-w-xl mx-4 rounded-2xl border border-border bg-surface shadow-elevated">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                  <Icon name="Bell" size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-text-primary">Notificaciones</h2>
                  <p className="text-xs text-text-secondary">Alertas recientes del sistema</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {notificationsCount > 0 && (
                  <button
                    type="button"
                    onClick={() => clearAllNotifications?.()}
                    className="text-xs text-text-secondary hover:text-text-primary"
                  >
                    Limpiar todo
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsNotificationsOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted"
                  aria-label="Cerrar"
                >
                  <Icon name="X" size={16} className="text-text-secondary" />
                </button>
              </div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-5 py-4 space-y-2 text-xs">
              {notificationsCount === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Icon name="Bell" size={36} className="mb-3 text-text-secondary" />
                  <p className="text-sm font-medium text-text-primary">No hay notificaciones</p>
                  <p className="text-xs text-text-secondary mt-1">Tus alertas aparecerán aquí cuando se generen.</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification?.id}
                    className="flex items-start gap-3 rounded-lg bg-muted/40 px-3 py-2"
                  >
                    <div className="mt-0.5">
                      <Icon
                        name={
                          notification?.type === 'success' ? 'CheckCircle' :
                          notification?.type === 'error' ? 'XCircle' :
                          notification?.type === 'warning' ? 'AlertTriangle' :
                          'Info'
                        }
                        size={16}
                        className={
                          notification?.type === 'success' ? 'text-success' :
                          notification?.type === 'error' ? 'text-error' :
                          notification?.type === 'warning' ? 'text-warning' :
                          'text-primary'
                        }
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      {notification?.title && (
                        <p className="text-[11px] font-semibold text-text-primary line-clamp-1">
                          {notification?.title}
                        </p>
                      )}
                      {notification?.message && (
                        <p className="text-[11px] text-text-secondary mt-0.5 break-words">
                          {notification?.message}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeNotification?.(notification?.id)}
                      className="ml-1 flex-shrink-0 text-text-secondary hover:text-text-primary"
                      aria-label="Eliminar notificación"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;